import './styles.scss'

import { Layout } from 'antd'
import type { JSX, PropsWithChildren } from 'react'
import { useEffect, useRef, useState } from 'react'
import Sticky from 'react-stickynode'

import { useAppSelector } from '@/store/hook'
import { getBreadcrumbs } from '@/store/selectors/breadcrumbSelectors'
import { useCheckLocation } from '@/hooks'
import useDeviceDetect from '@/hooks/useDeviceDetect'
import { useSearchContext } from '@/contexts/search/useSearchContext'
import { STICKY_Z_INDEX } from '@/libs/constants/header'
import {
  renderBannerHeight,
  renderBannerMarginHeight,
  renderBannerStickyTop,
  renderBreadcrumbMarginHeight,
  renderBreadcrumbStickyTop,
} from '@/libs/utils/breakpointsValue'

import B2becBreadcrumbs from '../B2becBreadcrumbs'

const { Content } = Layout

const SIDEBAR_HEADER_SELECTOR = '.sidebar__wrapper .sidebar__header'

function MainContent({ children }: PropsWithChildren): JSX.Element {
  const breadcrumbs = useAppSelector(getBreadcrumbs)

  const { isIpadLandscape, isMobile, isXLScreen } = useDeviceDetect()
  const { isSearchFocused } = useSearchContext()
  const { isInMyWorkspace } = useCheckLocation() ?? {}

  const isSearchFocusOnMobile = isSearchFocused && (isMobile || isIpadLandscape)

  const bannerRef = useRef<HTMLDivElement | null>(null)
  const [measuredBannerHeight, setMeasuredBannerHeight] = useState(0)

  useEffect(() => {
    const banner = bannerRef.current
    const title = document.querySelector<HTMLElement>(SIDEBAR_HEADER_SELECTOR)

    // Sidebar (and its title) only render at desktop widths.
    if (!isXLScreen || !banner || !title) {
      setMeasuredBannerHeight(0)
      return undefined
    }

    const measure = () => {
      const reach = title.getBoundingClientRect().bottom
        - banner.getBoundingClientRect().top
      setMeasuredBannerHeight(reach > 0 ? Math.ceil(reach) : 0)
    }

    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(title)
    window.addEventListener('resize', measure)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [isXLScreen, isInMyWorkspace, breadcrumbs])

  return (
    <div className="main-content__wrapper">
      <div className="main-content__header">
        <Sticky
          innerZ={STICKY_Z_INDEX.YELLOW_BANNER}
          top={renderBannerStickyTop(isInMyWorkspace, isIpadLandscape)}
        >
          {(stickyStatus) => (
            <div
              ref={bannerRef}
              className="main-content__wrapper__banner"
              style={{
                height: measuredBannerHeight
                  ? `${measuredBannerHeight}px`
                  : renderBannerHeight(
                    isInMyWorkspace,
                    breadcrumbs,
                    stickyStatus.status,
                  ),
                marginTop: isSearchFocusOnMobile
                  && renderBannerMarginHeight(
                    isInMyWorkspace,
                    stickyStatus.status,
                  ),
              }}
            />
          )}
        </Sticky>
      </div>

      <Content>
        <Sticky
          innerZ={STICKY_Z_INDEX.BREADCRUMB}
          top={renderBreadcrumbStickyTop()}
        >
          {(stickyStatus) => (
            <div
              className="main-content__breadcrumbs__wrapper"
              style={{
                marginTop: isSearchFocusOnMobile
                  && renderBreadcrumbMarginHeight(stickyStatus.status),
              }}
            >
              <B2becBreadcrumbs breadcrumbs={breadcrumbs} />
            </div>
          )}
        </Sticky>
        {children}
      </Content>
    </div>
  )
}

export default MainContent
